/**
 * Notifications Center Component
 * Hub for managing and sending notifications to users
 */

import React, { useEffect, useMemo, useState } from "react";
import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  useColorModeValue,
  VStack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Checkbox,
  Progress,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  CheckboxGroup,
  Icon,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { DeleteIcon, EditIcon, CopyIcon, ViewIcon, CloseIcon } from "@chakra-ui/icons";
import { AiOutlineSearch, AiOutlineDown } from "react-icons/ai";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import CardHeader from "../../../../components/Card/CardHeader";
import {
  cancelNotificationBroadcast,
  createNotificationBroadcast,
  fetchNotificationBroadcasts,
  fetchNotificationSegments,
  NotificationBroadcast,
  NotificationSegmentDescriptor,
  updateNotificationBroadcast,
} from "../../../../services/adminNotificationsApi";

interface Notification {
  id: string;
  type: "trade" | "system" | "performance" | "market" | "learning" | "announcement";
  title: string;
  message: string;
  time: string; // Human-readable time string (e.g., "15 minutes ago")
  createdAt: string; // ISO timestamp
  avatarName: string;
  avatarSrc: string;
  sentTo: "all" | "segmented";
  segments?: {
    subscriptionStatus?: string[];
    tradingActivity?: string[];
    tradingPerformance?: string[];
    accountAge?: string[];
    geographicLocation?: string[];
    userTier?: string[];
    engagementLevel?: string[];
    portfolioSize?: string[];
    brokerConnection?: string[];
    customTags?: string[];
  };
  isScheduled: boolean;
  scheduledFor?: string;
  status: "draft" | "sent" | "scheduled" | "sending" | "cancelled";
  /** Backend target_segment for edit/display when from API */
  targetSegment?: Record<string, unknown>;
  actionLink?: string; // Deep link URL for action button
  actionLabel?: string; // Label for action button (e.g., "View Trade", "Open Dashboard")
  totalSent: number; // Total number of users who received the notification (delivered)
  totalOpened: number; // Number of recipients who opened (read) the notification
  openRate?: number; // 0–100 from backend stats.open_rate
  details?: {
    tradeId?: string;
    symbol?: string;
    profit?: number;
    loss?: number;
    returnPercent?: number;
    entryPrice?: number;
    exitPrice?: number;
    quantity?: number;
    [key: string]: any;
  };
}

/** Map backend category (may use different names) back to UI type for display/filters. */
function backendCategoryToFormType(
  category: string
): Notification["type"] {
  const map: Record<string, Notification["type"]> = {
    marketing: "announcement",
    statistics: "performance",
    billing: "system",
    system: "system",
    trade: "trade",
    announcement: "announcement",
    performance: "performance",
    market: "market",
    learning: "learning",
  };
  return map[category] ?? "announcement";
}

interface NotificationsCenterProps {
  totalUsers?: number;
  activeUsers?: number;
  inactiveUsers?: number;
}

const NotificationsCenter: React.FC<NotificationsCenterProps> = ({ 
  totalUsers = 1247,
  activeUsers = 892,
  inactiveUsers = 355,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isTemplateOpen, onOpen: onTemplateOpen, onClose: onTemplateClose } = useDisclosure();
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [previewNotification, setPreviewNotification] = useState<Notification | null>(null);
  
  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<Notification["type"] | "all">("all");
  const [filterStatus, setFilterStatus] = useState<Notification["status"] | "all">("all");
  const [filterDateRange, setFilterDateRange] = useState<"all" | "today" | "week" | "month">("all");
  
  // Bulk Actions state
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set());
  
  // Templates state
  const [templates, setTemplates] = useState<Notification[]>([]);
  
  const textColor = useColorModeValue("#575757", "white");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const secondaryTextColor = useColorModeValue("gray.400", "gray.400");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const menuButtonHoverBg = useColorModeValue("gray.50", "gray.700");
  const menuButtonActiveBg = useColorModeValue("gray.100", "gray.600");
  const inputBg = useColorModeValue("white", "#151517");
  const menuBg = useColorModeValue("white", "#1A202C");
  const menuHoverBg = useColorModeValue("gray.50", "gray.700");
  const buttonHoverBg = useColorModeValue("gray.100", "gray.700");
  const cancelledBadgeBg = useColorModeValue("gray.300", "gray.600");
  const cancelledBadgeColor = useColorModeValue("gray.700", "gray.200");

  // Form state
  const [formData, setFormData] = useState({
    type: "announcement" as Notification["type"],
    title: "",
    message: "",
    avatarName: "Tessera",
    avatarSrc: "",
    sentTo: "all" as Notification["sentTo"],
    segments: {
      subscriptionStatus: [] as string[],
      tradingActivity: [] as string[],
      tradingPerformance: [] as string[],
      accountAge: [] as string[],
      geographicLocation: [] as string[],
      userTier: [] as string[],
      engagementLevel: [] as string[],
      portfolioSize: [] as string[],
      brokerConnection: [] as string[],
      customTags: [] as string[],
    },
    isScheduled: false,
    scheduledFor: "",
    actionLink: "",
    actionLabel: "",
    targetSegment: {} as Record<string, unknown>,
  });

  const [segmentDescriptor, setSegmentDescriptor] =
    useState<NotificationSegmentDescriptor | null>(null);

  // Broadcast list from API (Phase 2.2)
  const [broadcasts, setBroadcasts] = useState<NotificationBroadcast[]>([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(true);
  const [broadcastsError, setBroadcastsError] = useState<string | null>(null);
  const [broadcastsLoaded, setBroadcastsLoaded] = useState(false);

  /** Map backend broadcast to UI Notification for table display */
  const mapBroadcastToNotification = (b: NotificationBroadcast): Notification => {
    const createdAt = b.created_at;
    const sentAt = b.sent_at;
    const scheduledAt = b.scheduled_at;
    let time = formatTimeAgo(createdAt);
    if (b.status === "sent" && sentAt) {
      time = formatTimeAgo(sentAt);
    } else if ((b.status === "scheduled" || b.status === "sending") && scheduledAt) {
      time = `Scheduled for ${formatDate(scheduledAt)}`;
    }
    const stats = b.stats as { delivered?: number; opened_count?: number; open_rate?: number } | undefined;
    const totalSent = stats?.delivered ?? 0;
    const totalOpened = stats?.opened_count ?? (stats as { opened?: number } | undefined)?.opened ?? 0;
    const openRate = typeof stats?.open_rate === "number" ? stats.open_rate : undefined;
    const status =
      b.status === "sending"
        ? "scheduled"
        : (b.status as Notification["status"]);
    return {
      id: b.id,
      type: backendCategoryToFormType(b.category),
      title: b.title,
      message: b.body_markdown,
      time,
      createdAt,
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: b.target_segment && Object.keys(b.target_segment).length > 0 ? "segmented" : "all",
      isScheduled: b.status === "scheduled" || b.status === "sending",
      scheduledFor: scheduledAt || undefined,
      status,
      totalSent,
      totalOpened,
      openRate,
      targetSegment: b.target_segment ?? undefined,
    };
  };

  const refetchBroadcasts = React.useCallback(async () => {
    setBroadcastsLoading(true);
    setBroadcastsError(null);
    try {
      const statusParam =
        filterStatus === "all" ? undefined : (filterStatus as "draft" | "scheduled" | "sent");
      const list = await fetchNotificationBroadcasts({ status: statusParam });
      setBroadcasts(list);
      setBroadcastsLoaded(true);
    } catch (err) {
      setBroadcastsError(err instanceof Error ? err.message : "Failed to load broadcasts");
    } finally {
      setBroadcastsLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    refetchBroadcasts();
  }, [refetchBroadcasts]);

  // Refresh broadcast list periodically so open_rate / opened_count stay in sync
  useEffect(() => {
    const interval = setInterval(refetchBroadcasts, 60_000);
    return () => clearInterval(interval);
  }, [refetchBroadcasts]);

  useEffect(() => {
    let isMounted = true;
    const loadSegments = async () => {
      try {
        const desc = await fetchNotificationSegments();
        if (isMounted) setSegmentDescriptor(desc);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to load notification segment descriptor:", error);
      }
    };
    loadSegments();
    return () => {
      isMounted = false;
    };
  }, []);

  // Helper function to format time as "X ago" or date
  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Calculate total users that should receive notification based on sentTo
  // Note: "all" uses activeUsers as the current user base (892)
  const getTotalRecipients = (sentTo: Notification["sentTo"], segments?: Notification["segments"]): number => {
    switch (sentTo) {
      case "all":
        return activeUsers; // Use active users as the current user base
      case "segmented":
        // For now, return activeUsers as a placeholder
        // In production, this would calculate based on actual segment data
        return activeUsers;
      default:
        return activeUsers;
    }
  };

  // Get display text for notification's "Sent To"
  const getNotificationSentToText = (notification: Notification): string => {
    if (notification.sentTo === "all") {
      return "All Users";
    }
    // API-sourced: use targetSegment keys count
    const segmentKeys = (notification as Notification & { targetSegment?: Record<string, unknown> }).targetSegment;
    if (segmentKeys && Object.keys(segmentKeys).length > 0) {
      const n = Object.keys(segmentKeys).length;
      return `${n} Filter${n !== 1 ? "s" : ""}`;
    }
    if (notification.sentTo === "segmented" && notification.segments) {
      let count = 0;
      Object.values(notification.segments).forEach((segmentArray) => {
        count += segmentArray?.length || 0;
      });
      if (count === 0) {
        return "No Segments";
      }
      return `${count} Segment${count !== 1 ? "s" : ""}`;
    }
    return "All Users";
  };

  // Open rate: use backend stats.open_rate when present, else derive from opened/delivered
  const getOpenRate = (notification: Notification): number => {
    if (notification.status !== "sent") return 0;
    if (typeof notification.openRate === "number") return notification.openRate;
    if (notification.totalSent > 0) {
      return (notification.totalOpened / notification.totalSent) * 100;
    }
    return 0;
  };

  // Mock notifications data
  // Note: totalOpened stores the actual number of users who opened it
  // The open rate is calculated dynamically based on current user counts
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: "1",
      type: "announcement",
      title: "New Feature: Advanced Analytics",
      message: "We've added new advanced analytics features to help you track your trading performance better.",
      time: "2 days ago",
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: totalUsers,
      totalOpened: 634,
    },
    {
      id: "2",
      type: "system",
      title: "System Maintenance Scheduled",
      message: "We will be performing system maintenance on Saturday, December 30th from 2:00 AM to 4:00 AM EST.",
      time: "5 days ago",
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: totalUsers,
      totalOpened: 756,
    },
    {
      id: "3",
      type: "announcement",
      title: "Holiday Trading Hours",
      message: "Trading hours will be adjusted during the holiday season. Please check your broker's schedule.",
      time: "1 day ago",
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: activeUsers,
      totalOpened: 634,
    },
    {
      id: "4",
      type: "learning",
      title: "New Educational Content Available",
      message: "Check out our new trading guides and tutorials in the University section.",
      time: "In 2 days",
      createdAt: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: true,
      scheduledFor: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: "scheduled",
      totalSent: 0,
      totalOpened: 0,
    },
    {
      id: "5",
      type: "trade",
      title: "Trade Alert: Position Closed",
      message: "Your NVDA LONG position has been automatically closed. Profit: +$450.00 (+12.5%)",
      time: "3 hours ago",
      createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: totalUsers,
      totalOpened: 523,
      details: {
        tradeId: "trade-2024-155",
        symbol: "NVDA",
        profit: 450.00,
        returnPercent: 12.5,
      },
    },
    {
      id: "6",
      type: "performance",
      title: "Daily Goal Achieved",
      message: "Congratulations! You've reached your daily profit target of $500. Current: $523.50",
      time: "6 hours ago",
      createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: activeUsers,
      totalOpened: 445,
      details: {
        goalType: "daily_profit",
        target: 500,
        achieved: 523.50,
      },
    },
    {
      id: "7",
      type: "market",
      title: "Market Event: Earnings Announcement",
      message: "AAPL earnings announcement scheduled for tomorrow after market close. Prepare your positions accordingly.",
      time: "12 hours ago",
      createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: totalUsers,
      totalOpened: 678,
      details: {
        eventType: "earnings",
        symbol: "AAPL",
        date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      },
    },
    {
      id: "8",
      type: "system",
      title: "API Connection Restored",
      message: "Broker API connection has been restored. All systems operational. Downtime: 2 minutes",
      time: "1 day ago",
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: totalUsers,
      totalOpened: 801,
      details: {
        service: "broker_api",
        status: "connected",
        downtime: "2 minutes",
      },
    },
    {
      id: "9",
      type: "performance",
      title: "Win Streak Milestone",
      message: "Amazing! You're on a 7-trade winning streak 🎯 Total profit: $1,250.00",
      time: "1 day ago",
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: activeUsers,
      totalOpened: 389,
      details: {
        milestone: "win_streak",
        count: 7,
        totalProfit: 1250.00,
      },
    },
    {
      id: "10",
      type: "trade",
      title: "Trade Alert: Stop Loss Triggered",
      message: "Your TSLA SHORT position hit stop loss. Loss: -$125.00 (-5.1%)",
      time: "2 days ago",
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: totalUsers,
      totalOpened: 712,
      details: {
        tradeId: "trade-2024-154",
        symbol: "TSLA",
        loss: 125.00,
        returnPercent: -5.1,
      },
    },
    {
      id: "11",
      type: "learning",
      title: "Weekly Report Ready",
      message: "Your weekly trading report is ready for review. 23 trades, $1,850 profit",
      time: "2 days ago",
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: activeUsers,
      totalOpened: 567,
      details: {
        reportType: "weekly",
        totalTrades: 23,
        totalProfit: 1850.00,
      },
    },
    {
      id: "12",
      type: "market",
      title: "Market Hours Update",
      message: "Market closed early today - Holiday schedule. Regular hours resume tomorrow.",
      time: "3 days ago",
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "sent",
      totalSent: totalUsers,
      totalOpened: 623,
      details: {
        eventType: "market_hours",
        message: "Early close due to holiday",
      },
    },
    {
      id: "13",
      type: "announcement",
      title: "Platform Update: New Dashboard Features",
      message: "We've released new dashboard customization options. Personalize your trading view to match your style.",
      time: "In 1 day",
      createdAt: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: true,
      scheduledFor: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
      status: "scheduled",
      totalSent: 0,
      totalOpened: 0,
    },
    {
      id: "14",
      type: "system",
      title: "Security Update Required",
      message: "Please update your password for enhanced security. Click here to update now.",
      time: "In 3 days",
      createdAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: true,
      scheduledFor: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      status: "scheduled",
      totalSent: 0,
      totalOpened: 0,
    },
    {
      id: "15",
      type: "announcement",
      title: "Monthly Newsletter",
      message: "This month's trading insights and market analysis. Coming soon...",
      time: "Just now",
      createdAt: new Date().toISOString(),
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      isScheduled: false,
      status: "draft",
      totalSent: 0,
      totalOpened: 0,
    },
  ]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTargetSegmentChange = (fieldId: string, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      targetSegment: {
        ...(prev.targetSegment || {}),
        [fieldId]: value,
      },
    }));
  };

  const clearTargetSegmentField = (fieldId: string) => {
    setFormData((prev) => {
      const next = { ...(prev.targetSegment || {}) };
      delete next[fieldId];
      return { ...prev, targetSegment: next };
    });
  };

  const handleSegmentChange = (segmentType: keyof typeof formData.segments, values: string[]) => {
    setFormData((prev) => ({
      ...prev,
      segments: {
        ...prev.segments,
        [segmentType]: values,
      },
    }));
  };

  // Get selected segments count for display
  const getSelectedSegmentsCount = (): number => {
    let count = 0;
    Object.values(formData.segments).forEach((segmentArray) => {
      count += segmentArray.length;
    });
    return count;
  };

  // Get display text for "Send To" field
  const getSendToDisplayText = (): string => {
    if (formData.sentTo === "all") {
      return "All Users";
    }
    const keys = Object.keys(formData.targetSegment || {});
    if (keys.length === 0) return "Configure segment filters...";
    return `${keys.length} Filter${keys.length !== 1 ? "s" : ""} Selected`;
  };

  const handleCreateNotification = () => {
    setEditingNotification(null);
    setFormData({
      type: "announcement",
      title: "",
      message: "",
      avatarName: "Tessera",
      avatarSrc: "",
      sentTo: "all",
      segments: {
        subscriptionStatus: [],
        tradingActivity: [],
        tradingPerformance: [],
        accountAge: [],
        geographicLocation: [],
        userTier: [],
        engagementLevel: [],
        portfolioSize: [],
        brokerConnection: [],
        customTags: [],
      },
      isScheduled: false,
      scheduledFor: "",
      actionLink: "",
      actionLabel: "",
      targetSegment: {},
    });
    onOpen();
  };

  const handleEditNotification = (notification: Notification) => {
    setEditingNotification(notification);
    setFormData({
      type: notification.type,
      title: notification.title,
      message: notification.message,
      avatarName: notification.avatarName,
      avatarSrc: notification.avatarSrc,
      sentTo: notification.sentTo,
      segments: {
        subscriptionStatus: notification.segments?.subscriptionStatus || [],
        tradingActivity: notification.segments?.tradingActivity || [],
        tradingPerformance: notification.segments?.tradingPerformance || [],
        accountAge: notification.segments?.accountAge || [],
        geographicLocation: notification.segments?.geographicLocation || [],
        userTier: notification.segments?.userTier || [],
        engagementLevel: notification.segments?.engagementLevel || [],
        portfolioSize: notification.segments?.portfolioSize || [],
        brokerConnection: notification.segments?.brokerConnection || [],
        customTags: notification.segments?.customTags || [],
      },
      isScheduled: notification.isScheduled,
      scheduledFor: notification.scheduledFor || "",
      actionLink: notification.actionLink || "",
      actionLabel: notification.actionLabel || "",
      targetSegment: (notification as any).targetSegment || {},
    });
    onOpen();
  };

  const handleCopyNotification = (notification: Notification) => {
    setEditingNotification(null); // Create new notification, don't edit existing one
    setFormData({
      type: notification.type,
      title: notification.title,
      message: notification.message,
      avatarName: notification.avatarName,
      avatarSrc: notification.avatarSrc,
      sentTo: notification.sentTo,
      segments: {
        subscriptionStatus: notification.segments?.subscriptionStatus || [],
        tradingActivity: notification.segments?.tradingActivity || [],
        tradingPerformance: notification.segments?.tradingPerformance || [],
        accountAge: notification.segments?.accountAge || [],
        geographicLocation: notification.segments?.geographicLocation || [],
        userTier: notification.segments?.userTier || [],
        engagementLevel: notification.segments?.engagementLevel || [],
        portfolioSize: notification.segments?.portfolioSize || [],
        brokerConnection: notification.segments?.brokerConnection || [],
        customTags: notification.segments?.customTags || [],
      },
      isScheduled: notification.isScheduled,
      scheduledFor: notification.scheduledFor || "",
      actionLink: notification.actionLink || "",
      actionLabel: notification.actionLabel || "",
      targetSegment: (notification as { targetSegment?: Record<string, unknown> }).targetSegment ?? {},
    });
    onOpen();
  };

  /** Safely get a string message from a caught error (avoids "[object Object]"). */
  const getErrorMessage = (err: unknown): string => {
    let msg = "Something went wrong. Please try again.";
    if (err instanceof Error) msg = err.message;
    else if (typeof err === "string") msg = err;
    else if (err && typeof err === "object") {
      const o = err as Record<string, unknown>;
      if (typeof o.message === "string") msg = o.message;
      else if (typeof o.detail === "string") msg = o.detail;
      else if (o.detail && typeof o.detail === "object" && typeof (o.detail as Record<string, unknown>).message === "string") {
        msg = (o.detail as { message: string }).message;
      }
    }
    // Network-level "Failed to fetch" → often CORS, wrong path, or endpoint not mounted
    if (/failed to fetch|network error|load failed/i.test(msg)) {
      return "Request failed (network error). If other API calls work, open DevTools → Network, retry sending, and check the failing request: confirm the URL (e.g. …/api/admin/notification-broadcasts) and that the backend mounts admin notification routes at the same base URL.";
    }
    return msg;
  };

  const handleSaveNotification = async (action: "draft" | "schedule" | "send") => {
    if (!formData.title || !formData.message) {
      toastError("Validation Error", { description: "Please fill in all required fields." });
      return;
    }

    // Validate schedule date if scheduling
    if (action === "schedule" && !formData.scheduledFor) {
      toastError("Validation Error", { description: "Please select a date and time for scheduling." });
      return;
    }

    // Backend expects a dict; use {} for "all users", segment object for "segmented"
    const target_segment =
      formData.sentTo === "segmented" ? (formData.targetSegment || {}) : {};

    if (editingNotification) {
      // Update existing notification
      let newStatus: Notification["status"] = "draft";
      let currentRecipients = 0;
      
      if (action === "send") {
        newStatus = "sent";
        currentRecipients = getTotalRecipients(formData.sentTo, formData.segments);
      } else if (action === "schedule") {
        newStatus = "scheduled";
      } else {
        newStatus = "draft";
      }

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === editingNotification.id
            ? {
                ...n,
                ...formData,
                time: action === "schedule" && formData.scheduledFor
                  ? `Scheduled for ${formatDate(formData.scheduledFor)}`
                  : action === "send"
                  ? "Just now"
                  : formatTimeAgo(n.createdAt),
                scheduledFor: action === "schedule" ? formData.scheduledFor : undefined,
                status: newStatus,
                totalSent: action === "send" ? currentRecipients : n.totalSent,
                totalOpened: action === "send" ? 0 : n.totalOpened,
              }
            : n
        )
      );
      // Persist as broadcast first so we can use backend response for toast
      try {
        const updated = await updateNotificationBroadcast(editingNotification.id, {
          title: formData.title,
          body_markdown: formData.message,
          category: formData.type,
          target_segment,
          status: newStatus,
          scheduled_at:
            action === "schedule" && formData.scheduledFor
              ? formData.scheduledFor
              : null,
        });
        const delivered = updated.stats && typeof (updated.stats as { delivered?: number }).delivered === "number"
          ? (updated.stats as { delivered: number }).delivered
          : null;
        toast.success("Notification Updated", { description: action === "send"
            ? (delivered != null
                ? `The notification has been sent to ${delivered.toLocaleString()} users.`
                : "The notification has been sent. Delivery stats will update when available.")
            : action === "schedule"
            ? "The notification has been scheduled."
            : "The notification has been saved as a draft." });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to update notification broadcast:", error);
        presentMutationError(error, "Update failed");
      }
      if (broadcastsLoaded) {
        await refetchBroadcasts();
      }
    } else {
      // Create broadcast first so we use backend ID
      const createdAt = new Date().toISOString();
      let newStatus: Notification["status"] = "draft";
      let currentRecipients = 0;
      
      if (action === "send") {
        newStatus = "sent";
        currentRecipients = getTotalRecipients(formData.sentTo, formData.segments);
      } else if (action === "schedule") {
        newStatus = "scheduled";
      }

      try {
        const created = await createNotificationBroadcast({
          title: formData.title,
          body_markdown: formData.message,
          category: formData.type,
          target_segment,
          status: newStatus,
          scheduled_at:
            action === "schedule" && formData.scheduledFor
              ? formData.scheduledFor
              : null,
        });

        const delivered = created.stats && typeof (created.stats as { delivered?: number }).delivered === "number"
          ? (created.stats as { delivered: number }).delivered
          : null;
        const newNotification: Notification = {
          id: created.id,
          ...formData,
          segments: formData.sentTo === "segmented" ? formData.segments : undefined,
          time:
            action === "schedule" && formData.scheduledFor
              ? `Scheduled for ${formatDate(formData.scheduledFor)}`
              : "Just now",
          createdAt,
          scheduledFor: action === "schedule" ? formData.scheduledFor : undefined,
          status: newStatus,
          totalSent: action === "send" ? (delivered ?? 0) : 0,
          totalOpened: 0,
          // Store for future editing
          ...(target_segment ? { targetSegment: target_segment as any } : {}),
        } as any;

        setNotifications((prev) => [newNotification, ...prev]);

        toast.success("Notification Created", { description: action === "send"
            ? (delivered != null
                ? `The notification has been sent to ${delivered.toLocaleString()} users.`
                : "The notification has been sent. Delivery stats will update when available.")
            : action === "schedule"
            ? "The notification has been scheduled."
            : "The notification has been saved as a draft." });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to create notification broadcast:", error);
        const errMsg = getErrorMessage(error);
        const isGeneric500 = /500|Internal Server Error/i.test(errMsg);
        toastError("Send failed", { description: isGeneric500
            ? `${errMsg} Open the browser Console (F12) and look for "[Notifications] POST ... failed" to see the request payload and full server response to share with the backend.`
            : errMsg });
      }
      if (broadcastsLoaded) {
        await refetchBroadcasts();
      }
    }

    onClose();
  };


  const handleDeleteNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    toast.success("Notification Deleted", { description: "The notification has been deleted." });
  };

  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const handleCancelScheduled = async (id: string) => {
    setCancellingId(id);
    try {
      await cancelNotificationBroadcast(id);
      toast.success("Broadcast cancelled", { description: "The scheduled broadcast has been cancelled." });
      await refetchBroadcasts();
    } catch (err) {
      presentMutationError(err, "Failed to cancel");
    } finally {
      setCancellingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Display list: from API when loaded, else mock
  const displayList = useMemo(() => {
    if (broadcastsLoaded) {
      return broadcasts.map(mapBroadcastToNotification);
    }
    return notifications;
  }, [broadcastsLoaded, broadcasts, notifications]);

  // Filter and sort notifications
  const filteredAndSortedNotifications = useMemo(() => {
    let filtered = [...displayList];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(query) ||
          n.message.toLowerCase().includes(query)
      );
    }

    // Type filter
    if (filterType !== "all") {
      filtered = filtered.filter((n) => n.type === filterType);
    }

    // Status filter (treat "sending" as scheduled)
    if (filterStatus !== "all") {
      filtered = filtered.filter((n) => {
        const s = n.status;
        if (filterStatus === "scheduled") return s === "scheduled" || s === "sending";
        return s === filterStatus;
      });
    }

    // Date range filter
    if (filterDateRange !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);

      filtered = filtered.filter((n) => {
        const createdAt = new Date(n.createdAt);
        switch (filterDateRange) {
          case "today":
            return createdAt >= today;
          case "week":
            return createdAt >= weekAgo;
          case "month":
            return createdAt >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Sort: scheduled first, then by createdAt (newest first)
    return filtered.sort((a, b) => {
      const aSched = a.status === "scheduled" || a.status === "sending";
      const bSched = b.status === "scheduled" || b.status === "sending";
      if (aSched && !bSched) return -1;
      if (!aSched && bSched) return 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [displayList, searchQuery, filterType, filterStatus, filterDateRange]);

  const getTypeBadge = (type: Notification["type"]) => {
    const configs: Record<Notification["type"], { gradient: string; label: string }> = {
      trade: {
        gradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
        label: "Trade",
      },
      system: {
        gradient: "linear-gradient(135deg, #6B7280, #9CA3AF)",
        label: "System",
      },
      performance: {
        gradient: "linear-gradient(135deg, #06E363, #AAFAB1)",
        label: "Performance",
      },
      market: {
        gradient: "linear-gradient(135deg, #F59E0B, #FBBF24)",
        label: "Market",
      },
      learning: {
        gradient: "linear-gradient(135deg, #8B5CF6, #A78BFA)",
        label: "Learning",
      },
      announcement: {
        gradient: "linear-gradient(135deg, #E00101, #FE4244)",
        label: "Announcement",
      },
    };
    const config = configs[type] || configs.system;
    return (
      <Badge
        bgGradient={config.gradient}
        color="white"
        fontFamily="'IBM Plex Sans', sans-serif"
        fontSize="xs"
        px={2}
        py={1}
        borderRadius="full"
        fontWeight="medium"
        textTransform="uppercase"
      >
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (status: Notification["status"]) => {
    if (status === "sent") {
      return (
        <Badge
          bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
          color="white"
          fontFamily="'IBM Plex Sans', sans-serif"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="full"
          fontWeight="medium"
          textTransform="uppercase"
        >
          Sent
        </Badge>
      );
    } else if (status === "scheduled" || status === "sending") {
      return (
        <Badge
          bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
          color="white"
          fontFamily="'IBM Plex Sans', sans-serif"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="full"
          fontWeight="medium"
          textTransform="uppercase"
        >
          {status === "sending" ? "Sending…" : "Scheduled"}
        </Badge>
      );
    } else if (status === "cancelled") {
      return (
        <Badge
          bg={cancelledBadgeBg}
          color={cancelledBadgeColor}
          fontFamily="'IBM Plex Sans', sans-serif"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="full"
          fontWeight="medium"
          textTransform="uppercase"
        >
          Cancelled
        </Badge>
      );
    } else {
      // draft
      return (
        <Badge
          bg="transparent"
          color={secondaryTextColor}
          fontFamily="'IBM Plex Sans', sans-serif"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="full"
          fontWeight="medium"
          textTransform="uppercase"
          border="1px solid"
          borderColor={borderColor}
        >
          Draft
        </Badge>
      );
    }
  };

  // Calculate analytics metrics (use display list so API data is reflected)
  const analyticsMetrics = useMemo(() => {
    const sent = displayList.filter(n => n.status === "sent");
    const scheduled = displayList.filter(n => n.status === "scheduled" || n.status === "sending");
    const drafts = displayList.filter(n => n.status === "draft");
    
    const totalSent = sent.reduce((sum, n) => sum + n.totalSent, 0);
    const totalOpened = sent.reduce((sum, n) => sum + n.totalOpened, 0);
    const avgOpenRate = sent.length > 0 
      ? sent.reduce((sum, n) => sum + getOpenRate(n), 0) / sent.length 
      : 0;
    
    return {
      totalNotifications: displayList.length,
      sent: sent.length,
      scheduled: scheduled.length,
      drafts: drafts.length,
      totalSent,
      totalOpened,
      avgOpenRate,
    };
  }, [displayList]);

  return (
    <VStack align="stretch" spacing={6}>
      {/* Analytics Dashboard */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <Card bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
          <CardBody p={4}>
            <VStack align="start" spacing={1}>
              <Text
                fontSize="xs"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="medium"
                color={secondaryTextColor}
                textTransform="uppercase"
              >
                Total Notifications
              </Text>
              <Text
                fontSize="2xl"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="700"
                color={textColor}
                lineHeight="1"
              >
                {analyticsMetrics.totalNotifications}
              </Text>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
          <CardBody p={4}>
            <VStack align="start" spacing={1}>
              <Text
                fontSize="xs"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="medium"
                color={secondaryTextColor}
                textTransform="uppercase"
              >
                Sent
              </Text>
              <Text
                fontSize="2xl"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="700"
                bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                bgClip="text"
                lineHeight="1"
              >
                {analyticsMetrics.sent}
              </Text>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
          <CardBody p={4}>
            <VStack align="start" spacing={1}>
              <Text
                fontSize="xs"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="medium"
                color={secondaryTextColor}
                textTransform="uppercase"
              >
                Total Sent
              </Text>
              <Text
                fontSize="2xl"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="700"
                color={textColor}
                lineHeight="1"
              >
                {analyticsMetrics.totalSent.toLocaleString()}
              </Text>
            </VStack>
          </CardBody>
        </Card>

        <Card bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
          <CardBody p={4}>
            <VStack align="start" spacing={1}>
              <Text
                fontSize="xs"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="medium"
                color={secondaryTextColor}
                textTransform="uppercase"
              >
                Avg Open Rate
              </Text>
              <Text
                fontSize="2xl"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="700"
                bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                bgClip="text"
                lineHeight="1"
              >
                {analyticsMetrics.avgOpenRate.toFixed(1)}%
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Header */}
      <Text
        fontSize="sm"
        color={secondaryTextColor}
        fontWeight="medium"
        fontFamily="'IBM Plex Sans', sans-serif"
        textTransform="uppercase"
        mb={4}
      >
        Notifications Center
      </Text>

      {/* Search & Filter Bar */}
      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        p={{ base: 3, md: 4 }}
        w="100%"
        mb={4}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={4}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
        >
          {/* Left side: Filters and Search */}
          <Box flex={{ base: "none", md: "1" }} w="100%">
            {/* Mobile: Use Grid for equal-width columns */}
            <Box 
              display={{ base: "grid", sm: "none" }} 
              w="100%" 
              gridTemplateColumns="calc(50% - 6px) calc(50% - 6px)" 
              gap="12px"
              minW="0"
              maxW="100%"
              boxSizing="border-box"
            >
              {/* Type Filter - Mobile */}
              <Box w="100%" minW="0" maxW="100%" boxSizing="border-box">
                <Menu matchWidth>
                  <MenuButton
                    as={Button}
                    size="sm"
                    bg={cardBg}
                    w="100%"
                    minW="0"
                    maxW="100%"
                    h="32px"
                    px="12px"
                    py="8px"
                    fontSize="xs"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    textAlign="left"
                    justifyContent="space-between"
                    _hover={{ bg: buttonHoverBg }}
                    _active={{ bg: cardBg }}
                    _focus={{ bg: cardBg, boxShadow: "0 0 0 1px #3EEAEC" }}
                    rightIcon={<Icon as={AiOutlineDown} w={4} h={4} color={secondaryTextColor} />}
                  >
                    {filterType === "all" ? "All Types" : filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                  </MenuButton>
                  <MenuList maxH="200px" overflowY="auto" bg={menuBg} borderColor={borderColor}>
                    <MenuItemOption
                      value="all"
                      onClick={() => setFilterType("all")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      All Types
                    </MenuItemOption>
                    <MenuItemOption
                      value="announcement"
                      onClick={() => setFilterType("announcement")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Announcement
                    </MenuItemOption>
                    <MenuItemOption
                      value="system"
                      onClick={() => setFilterType("system")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      System
                    </MenuItemOption>
                    <MenuItemOption
                      value="trade"
                      onClick={() => setFilterType("trade")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Trade
                    </MenuItemOption>
                    <MenuItemOption
                      value="performance"
                      onClick={() => setFilterType("performance")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Performance
                    </MenuItemOption>
                    <MenuItemOption
                      value="market"
                      onClick={() => setFilterType("market")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Market
                    </MenuItemOption>
                    <MenuItemOption
                      value="learning"
                      onClick={() => setFilterType("learning")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Learning
                    </MenuItemOption>
                  </MenuList>
                </Menu>
              </Box>

              {/* Status Filter - Mobile */}
              <Box w="100%" minW="0" maxW="100%" boxSizing="border-box">
                <Menu matchWidth>
                  <MenuButton
                    as={Button}
                    size="sm"
                    bg={cardBg}
                    w="100%"
                    minW="0"
                    maxW="100%"
                    h="32px"
                    px="12px"
                    py="8px"
                    fontSize="xs"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    textAlign="left"
                    justifyContent="space-between"
                    _hover={{ bg: buttonHoverBg }}
                    _active={{ bg: cardBg }}
                    _focus={{ bg: cardBg, boxShadow: "0 0 0 1px #3EEAEC" }}
                    rightIcon={<Icon as={AiOutlineDown} w={4} h={4} color={secondaryTextColor} />}
                  >
                    {filterStatus === "all" ? "All Status" : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
                  </MenuButton>
                  <MenuList maxH="200px" overflowY="auto" bg={menuBg} borderColor={borderColor}>
                    <MenuItemOption
                      value="all"
                      onClick={() => setFilterStatus("all")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      All Status
                    </MenuItemOption>
                    <MenuItemOption
                      value="draft"
                      onClick={() => setFilterStatus("draft")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Draft
                    </MenuItemOption>
                    <MenuItemOption
                      value="scheduled"
                      onClick={() => setFilterStatus("scheduled")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Scheduled
                    </MenuItemOption>
                    <MenuItemOption
                      value="sent"
                      onClick={() => setFilterStatus("sent")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Sent
                    </MenuItemOption>
                  </MenuList>
                </Menu>
              </Box>
            </Box>

            {/* Search Bar - Mobile (outside grid, below filter boxes) */}
            <Box w="100%" display={{ base: "block", sm: "none" }} mt={3}>
              <InputGroup w="100%">
                <InputLeftElement pointerEvents="none" h="100%" alignItems="center" justifyContent="center">
                  <Icon as={AiOutlineSearch} color={secondaryTextColor} />
                </InputLeftElement>
                <Input
                  placeholder="Search notifications"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg={inputBg}
                  size="sm"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  color={textColor}
                  fontSize="xs"
                  textTransform="uppercase"
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="md"
                  _placeholder={{ color: secondaryTextColor, fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: "medium", textTransform: "uppercase" }}
                  _focus={{ borderColor: "#3EEAEC" }}
                />
              </InputGroup>
            </Box>

            {/* Desktop: Use Flex layout */}
            <Flex
              direction="row"
              gap={3}
              align="center"
              flexWrap="wrap"
              w="100%"
              display={{ base: "none", sm: "flex" }}
            >
              {/* Type Filter - Desktop */}
              <Box flex="0 0 120px">
                <Menu>
                  <MenuButton
                    as={Button}
                    size="sm"
                    bg={cardBg}
                    w="100%"
                    h="32px"
                    px="12px"
                    py="8px"
                    fontSize="xs"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    textAlign="left"
                    justifyContent="space-between"
                    _hover={{ bg: buttonHoverBg }}
                    _active={{ bg: cardBg }}
                    _focus={{ bg: cardBg, boxShadow: "0 0 0 1px #3EEAEC" }}
                    rightIcon={<Icon as={AiOutlineDown} w={4} h={4} color={secondaryTextColor} />}
                  >
                    {filterType === "all" ? "All Types" : filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                  </MenuButton>
                  <MenuList maxH="200px" overflowY="auto" bg={menuBg} borderColor={borderColor}>
                    <MenuItemOption
                      value="all"
                      onClick={() => setFilterType("all")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      All Types
                    </MenuItemOption>
                    <MenuItemOption
                      value="announcement"
                      onClick={() => setFilterType("announcement")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Announcement
                    </MenuItemOption>
                    <MenuItemOption
                      value="system"
                      onClick={() => setFilterType("system")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      System
                    </MenuItemOption>
                    <MenuItemOption
                      value="trade"
                      onClick={() => setFilterType("trade")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Trade
                    </MenuItemOption>
                    <MenuItemOption
                      value="performance"
                      onClick={() => setFilterType("performance")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Performance
                    </MenuItemOption>
                    <MenuItemOption
                      value="market"
                      onClick={() => setFilterType("market")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Market
                    </MenuItemOption>
                    <MenuItemOption
                      value="learning"
                      onClick={() => setFilterType("learning")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Learning
                    </MenuItemOption>
                  </MenuList>
                </Menu>
              </Box>

              {/* Status Filter - Desktop */}
              <Box flex="0 0 120px">
                <Menu>
                  <MenuButton
                    as={Button}
                    size="sm"
                    bg={cardBg}
                    w="100%"
                    h="32px"
                    px="12px"
                    py="8px"
                    fontSize="xs"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    textAlign="left"
                    justifyContent="space-between"
                    _hover={{ bg: buttonHoverBg }}
                    _active={{ bg: cardBg }}
                    _focus={{ bg: cardBg, boxShadow: "0 0 0 1px #3EEAEC" }}
                    rightIcon={<Icon as={AiOutlineDown} w={4} h={4} color={secondaryTextColor} />}
                  >
                    {filterStatus === "all" ? "All Status" : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
                  </MenuButton>
                  <MenuList maxH="200px" overflowY="auto" bg={menuBg} borderColor={borderColor}>
                    <MenuItemOption
                      value="all"
                      onClick={() => setFilterStatus("all")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      All Status
                    </MenuItemOption>
                    <MenuItemOption
                      value="draft"
                      onClick={() => setFilterStatus("draft")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Draft
                    </MenuItemOption>
                    <MenuItemOption
                      value="scheduled"
                      onClick={() => setFilterStatus("scheduled")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Scheduled
                    </MenuItemOption>
                    <MenuItemOption
                      value="sent"
                      onClick={() => setFilterStatus("sent")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Sent
                    </MenuItemOption>
                  </MenuList>
                </Menu>
              </Box>

              {/* Date Range Filter - Desktop */}
              <Box flex="0 0 140px">
                <Menu>
                  <MenuButton
                    as={Button}
                    size="sm"
                    bg={cardBg}
                    w="100%"
                    h="32px"
                    px="12px"
                    py="8px"
                    fontSize="xs"
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    textAlign="left"
                    justifyContent="space-between"
                    _hover={{ bg: buttonHoverBg }}
                    _active={{ bg: cardBg }}
                    _focus={{ bg: cardBg, boxShadow: "0 0 0 1px #3EEAEC" }}
                    rightIcon={<Icon as={AiOutlineDown} w={4} h={4} color={secondaryTextColor} />}
                  >
                    {filterDateRange === "all" ? "All Time" : filterDateRange === "today" ? "Today" : filterDateRange === "week" ? "Last 7 Days" : "Last 30 Days"}
                  </MenuButton>
                  <MenuList maxH="200px" overflowY="auto" bg={menuBg} borderColor={borderColor}>
                    <MenuItemOption
                      value="all"
                      onClick={() => setFilterDateRange("all")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      All Time
                    </MenuItemOption>
                    <MenuItemOption
                      value="today"
                      onClick={() => setFilterDateRange("today")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Today
                    </MenuItemOption>
                    <MenuItemOption
                      value="week"
                      onClick={() => setFilterDateRange("week")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Last 7 Days
                    </MenuItemOption>
                    <MenuItemOption
                      value="month"
                      onClick={() => setFilterDateRange("month")}
                      fontSize="xs"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      fontWeight="medium"
                      color={secondaryTextColor}
                      textTransform="uppercase"
                      bg={menuBg}
                      _hover={{ bg: menuHoverBg }}
                    >
                      Last 30 Days
                    </MenuItemOption>
                  </MenuList>
                </Menu>
              </Box>

              {/* Search Bar - Desktop */}
              <InputGroup flex="1" maxW={{ base: "100%", sm: "250px" }} display={{ base: "none", sm: "flex" }}>
                <InputLeftElement pointerEvents="none" h="100%" alignItems="center" justifyContent="center">
                  <Icon as={AiOutlineSearch} color={secondaryTextColor} />
                </InputLeftElement>
                <Input
                  placeholder="Search notifications"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg={inputBg}
                  size="sm"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  color={textColor}
                  fontSize="xs"
                  textTransform="uppercase"
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="md"
                  _placeholder={{ color: secondaryTextColor, fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: "medium", textTransform: "uppercase" }}
                  _focus={{ borderColor: "#3EEAEC" }}
                />
              </InputGroup>
            </Flex>
          </Box>
          
          {/* Right side: Action Buttons */}
          <HStack spacing={2} flexShrink={0}>
            <Button
              bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
              color="white"
              onClick={handleCreateNotification}
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              textTransform="uppercase"
              fontSize="xs"
              size="sm"
              _hover={{ opacity: 0.9 }}
            >
              Create Notification
            </Button>
            <Button
              variant="outline"
              onClick={onTemplateOpen}
              fontFamily="'IBM Plex Sans', sans-serif"
              fontWeight="medium"
              textTransform="uppercase"
              fontSize="xs"
              borderColor={borderColor}
              size="sm"
            >
              Templates
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Bulk Actions Bar */}
      {selectedNotifications.size > 0 && (
        <Card bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor} mb={4}>
          <CardBody p={4}>
            <HStack justify="space-between" align="center">
              <Text
                fontFamily="'IBM Plex Sans', sans-serif"
                fontSize="sm"
                color={textColor}
                fontWeight="medium"
              >
                {selectedNotifications.size} notification{selectedNotifications.size !== 1 ? 's' : ''} selected
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => {
                    setNotifications(prev => prev.filter(n => !selectedNotifications.has(n.id)));
                    setSelectedNotifications(new Set());
                    toast.success("Notifications Deleted", { description: `${selectedNotifications.size} notification(s) have been deleted.` });
                  }}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontSize="xs"
                  fontWeight="medium"
                >
                  Delete Selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedNotifications(new Set())}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontSize="xs"
                  fontWeight="medium"
                >
                  Clear Selection
                </Button>
              </HStack>
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* Broadcast list error */}
      {broadcastsError && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertTitle>Could not load broadcasts</AlertTitle>
          <AlertDescription>{broadcastsError}. Showing sample data.</AlertDescription>
        </Alert>
      )}

      {/* Notifications Table */}
      <Card bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
        <CardHeader pb={4}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Text
              fontSize="sm"
              color={secondaryTextColor}
              fontWeight="medium"
              fontFamily="'IBM Plex Sans', sans-serif"
              textTransform="uppercase"
            >
              {broadcastsLoaded ? "Broadcasts" : "All Notifications"}
            </Text>
            {broadcastsLoading && (
              <Spinner size="sm" color="cyan.500" />
            )}
          </Flex>
        </CardHeader>
        <CardBody p={0}>
          <Box overflowX="auto" w="100%">
            <Table variant="simple" w="100%">
              <Thead bg={cardBg}>
                <Tr>
                  <Th
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    fontSize="xs"
                    pb={3}
                    w="50px"
                  >
                    <Checkbox
                      isChecked={selectedNotifications.size > 0 && selectedNotifications.size === filteredAndSortedNotifications.length}
                      isIndeterminate={selectedNotifications.size > 0 && selectedNotifications.size < filteredAndSortedNotifications.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedNotifications(new Set(filteredAndSortedNotifications.map(n => n.id)));
                        } else {
                          setSelectedNotifications(new Set());
                        }
                      }}
                      colorScheme="cyan"
                    />
                  </Th>
                  <Th
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    fontSize="xs"
                    pb={3}
                  >
                    Type
                  </Th>
                  <Th
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    fontSize="xs"
                    pb={3}
                  >
                    Title
                  </Th>
                  <Th
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    fontSize="xs"
                    pb={3}
                  >
                    Sent To
                  </Th>
                  <Th
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    fontSize="xs"
                    pb={3}
                  >
                    Status
                  </Th>
                  <Th
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    fontSize="xs"
                    pb={3}
                  >
                    Time
                  </Th>
                  <Th
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    fontSize="xs"
                    pb={3}
                    w="200px"
                  >
                    Open Rate
                  </Th>
                  <Th
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={secondaryTextColor}
                    textTransform="uppercase"
                    fontSize="xs"
                    pb={3}
                    textAlign="center"
                  >
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredAndSortedNotifications.map((notification) => (
                  <Tr key={notification.id} _hover={{ bg: rowHoverBg }} transition="background-color 0.2s">
                    <Td>
                      <Checkbox
                        isChecked={selectedNotifications.has(notification.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedNotifications);
                          if (e.target.checked) {
                            newSelected.add(notification.id);
                          } else {
                            newSelected.delete(notification.id);
                          }
                          setSelectedNotifications(newSelected);
                        }}
                        colorScheme="cyan"
                      />
                    </Td>
                    <Td>
                      {getTypeBadge(notification.type)}
                    </Td>
                    <Td
                      fontFamily="'IBM Plex Sans', sans-serif"
                      color={textColor}
                      fontWeight="medium"
                    >
                      {notification.title}
                    </Td>
                    <Td
                      fontFamily="'IBM Plex Sans', sans-serif"
                      color={textColor}
                    >
                      {getNotificationSentToText(notification)}
                    </Td>
                    <Td>{getStatusBadge(notification.status)}</Td>
                    <Td
                      fontFamily="'IBM Plex Sans', sans-serif"
                      color={secondaryTextColor}
                      fontSize="sm"
                    >
                      {notification.time}
                    </Td>
                    <Td>
                      {notification.status === "sent" ? (
                        <VStack align="stretch" spacing={1}>
                          <HStack justify="space-between" w="100%">
                            <Text
                              fontFamily="'IBM Plex Sans', sans-serif"
                              fontSize="xs"
                              color={secondaryTextColor}
                              fontWeight="medium"
                            >
                              {getOpenRate(notification).toFixed(1)}%
                            </Text>
                            <Text
                              fontFamily="'IBM Plex Sans', sans-serif"
                              fontSize="xs"
                              color={secondaryTextColor}
                            >
                              {notification.totalOpened.toLocaleString()} of {notification.totalSent.toLocaleString()} opened
                            </Text>
                          </HStack>
                          <Box
                            position="relative"
                            w="100%"
                            h="8px"
                            bg={useColorModeValue("gray.200", "gray.700")}
                            borderRadius="full"
                            overflow="hidden"
                          >
                            <Box
                              h="100%"
                              w={`${Math.min(100, getOpenRate(notification))}%`}
                              bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                              borderRadius="full"
                              transition="width 0.3s ease"
                            />
                          </Box>
                        </VStack>
                      ) : (
                        <Text
                          fontFamily="'IBM Plex Sans', sans-serif"
                          fontSize="xs"
                          color={secondaryTextColor}
                          fontStyle="italic"
                        >
                          {notification.status === "draft" ? "Not sent yet" : "Scheduled"}
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <HStack justify="center" spacing={2} flexWrap="wrap">
                        {notification.status !== "sent" && notification.status !== "cancelled" && (
                          <IconButton
                            aria-label="Edit notification"
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditNotification(notification)}
                          />
                        )}
                        {(notification.status === "scheduled" || notification.status === "sending") && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="red"
                            leftIcon={<CloseIcon />}
                            isLoading={cancellingId === notification.id}
                            onClick={() => handleCancelScheduled(notification.id)}
                          >
                            Cancel
                          </Button>
                        )}
                        <IconButton
                          aria-label="Preview notification"
                          icon={<ViewIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setPreviewNotification(notification);
                            onPreviewOpen();
                          }}
                        />
                        <IconButton
                          aria-label="Copy notification"
                          icon={<CopyIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopyNotification(notification)}
                          title="Duplicate"
                        />
                        {notification.status === "sent" && (
                          <IconButton
                            aria-label="Resend notification"
                            icon={<CopyIcon />}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              handleCopyNotification(notification);
                              toast.info("Notification Duplicated", { description: "You can now modify and resend this notification." });
                            }}
                            title="Resend"
                          />
                        )}
                        <IconButton
                          aria-label="Delete notification"
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteNotification(notification.id)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader
            color="#38BBD1"
            fontFamily="'Urbanist', sans-serif"
            fontWeight="normal"
          >
            {editingNotification ? "Edit Notification" : "Create New Notification"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  color={textColor}
                >
                  Type
                </FormLabel>
                <Select
                  value={formData.type}
                  onChange={(e) =>
                    handleInputChange("type", e.target.value as Notification["type"])
                  }
                  bg={cardBg}
                  borderColor={borderColor}
                >
                  <option value="announcement">Announcement</option>
                  <option value="system">System</option>
                  <option value="trade">Trade</option>
                  <option value="performance">Performance</option>
                  <option value="market">Market</option>
                  <option value="learning">Learning</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  color={textColor}
                >
                  Title
                </FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="Enter notification title"
                  bg={cardBg}
                  borderColor={borderColor}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  color={textColor}
                >
                  Message
                </FormLabel>
                <Box
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="md"
                  _focusWithin={{
                    borderColor: "#38BBD1",
                    boxShadow: "0 0 0 1px #38BBD1",
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={formData.message}
                    onChange={(value) => handleInputChange("message", value)}
                    placeholder="Enter notification message"
                    style={{
                      fontFamily: "'IBM Plex Sans', sans-serif",
                    }}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ["bold", "italic", "underline", "strike"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ color: [] }, { background: [] }],
                        ["link"],
                        ["clean"],
                      ],
                    }}
                  />
                </Box>
                <Text
                  fontSize="xs"
                  color={secondaryTextColor}
                  mt={1}
                  fontFamily="'IBM Plex Sans', sans-serif"
                >
                  Format your message with rich text. HTML will be rendered in the notification.
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  color={textColor}
                >
                  Send To
                </FormLabel>
                <VStack spacing={3} align="stretch">
                  <Select
                    value={formData.sentTo}
                    onChange={(e) =>
                      handleInputChange("sentTo", e.target.value as Notification["sentTo"])
                    }
                    bg={cardBg}
                    borderColor={borderColor}
                  >
                    <option value="all">All Users</option>
                    <option value="segmented">Segmented Audience</option>
                  </Select>

                  {formData.sentTo === "segmented" && (
                    <Menu closeOnSelect={false}>
                      <MenuButton
                        as={Button}
                        w="100%"
                        bg={cardBg}
                        border="1px solid"
                        borderColor={borderColor}
                        textAlign="left"
                        fontWeight="normal"
                        fontFamily="'IBM Plex Sans', sans-serif"
                        _hover={{ bg: menuButtonHoverBg }}
                        _active={{ bg: menuButtonActiveBg }}
                      >
                        {getSendToDisplayText()}
                      </MenuButton>
                      <MenuList
                        minW="600px"
                        maxH="500px"
                        overflowY="auto"
                        bg={cardBg}
                        borderColor={borderColor}
                      >
                        <Box p={4}>
                          <Tabs>
                            <TabList
                              overflowX="auto"
                              borderBottom="1px solid"
                              borderColor={borderColor}
                            >
                              <Tab
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                color={secondaryTextColor}
                                _selected={{
                                  color: textColor,
                                  borderBottomColor: "#38BBD1",
                                }}
                              >
                                Subscription
                              </Tab>
                              <Tab
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                color={secondaryTextColor}
                                _selected={{
                                  color: textColor,
                                  borderBottomColor: "#38BBD1",
                                }}
                              >
                                Trading Activity
                              </Tab>
                              <Tab
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                color={secondaryTextColor}
                                _selected={{
                                  color: textColor,
                                  borderBottomColor: "#38BBD1",
                                }}
                              >
                                Performance
                              </Tab>
                              <Tab
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                color={secondaryTextColor}
                                _selected={{
                                  color: textColor,
                                  borderBottomColor: "#38BBD1",
                                }}
                              >
                                Account Age
                              </Tab>
                              <Tab
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                color={secondaryTextColor}
                                _selected={{
                                  color: textColor,
                                  borderBottomColor: "#38BBD1",
                                }}
                              >
                                Location
                              </Tab>
                              <Tab
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                color={secondaryTextColor}
                                _selected={{
                                  color: textColor,
                                  borderBottomColor: "#38BBD1",
                                }}
                              >
                                User Tier
                              </Tab>
                              <Tab
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                color={secondaryTextColor}
                                _selected={{
                                  color: textColor,
                                  borderBottomColor: "#38BBD1",
                                }}
                              >
                                Engagement
                              </Tab>
                              <Tab
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                color={secondaryTextColor}
                                _selected={{
                                  color: textColor,
                                  borderBottomColor: "#38BBD1",
                                }}
                              >
                                Portfolio
                              </Tab>
                              <Tab
                                fontSize="xs"
                                fontFamily="'IBM Plex Sans', sans-serif"
                                fontWeight="medium"
                                textTransform="uppercase"
                                color={secondaryTextColor}
                                _selected={{
                                  color: textColor,
                                  borderBottomColor: "#38BBD1",
                                }}
                              >
                                Broker
                              </Tab>
                            </TabList>

                            <TabPanels>
                              {/* Subscription Status */}
                              <TabPanel>
                                <CheckboxGroup
                                  value={formData.segments.subscriptionStatus}
                                  onChange={(values) =>
                                    handleSegmentChange("subscriptionStatus", values as string[])
                                  }
                                >
                                  <VStack align="start" spacing={2}>
                                    <Checkbox value="subscribed" colorScheme="cyan">
                                      Subscribed
                                    </Checkbox>
                                    <Checkbox value="notSubscribed" colorScheme="cyan">
                                      Not Subscribed
                                    </Checkbox>
                                    <Checkbox value="trial" colorScheme="cyan">
                                      Trial Users
                                    </Checkbox>
                                    <Checkbox value="expired" colorScheme="cyan">
                                      Expired Subscriptions
                                    </Checkbox>
                                  </VStack>
                                </CheckboxGroup>
                              </TabPanel>

                              {/* Trading Activity Level */}
                              <TabPanel>
                                <CheckboxGroup
                                  value={formData.segments.tradingActivity}
                                  onChange={(values) =>
                                    handleSegmentChange("tradingActivity", values as string[])
                                  }
                                >
                                  <VStack align="start" spacing={2}>
                                    <Checkbox value="highVolume" colorScheme="cyan">
                                      High Volume Traders (10+ trades/month)
                                    </Checkbox>
                                    <Checkbox value="mediumVolume" colorScheme="cyan">
                                      Medium Traders (5-9 trades/month)
                                    </Checkbox>
                                    <Checkbox value="lowVolume" colorScheme="cyan">
                                      Low Volume Traders (1-4 trades/month)
                                    </Checkbox>
                                    <Checkbox value="noTrades" colorScheme="cyan">
                                      No Trades
                                    </Checkbox>
                                  </VStack>
                                </CheckboxGroup>
                              </TabPanel>

                              {/* Trading Performance */}
                              <TabPanel>
                                <CheckboxGroup
                                  value={formData.segments.tradingPerformance}
                                  onChange={(values) =>
                                    handleSegmentChange("tradingPerformance", values as string[])
                                  }
                                >
                                  <VStack align="start" spacing={2}>
                                    <Checkbox value="profitable" colorScheme="cyan">
                                      Profitable Traders
                                    </Checkbox>
                                    <Checkbox value="unprofitable" colorScheme="cyan">
                                      Unprofitable Traders
                                    </Checkbox>
                                    <Checkbox value="breakEven" colorScheme="cyan">
                                      Break-Even Traders
                                    </Checkbox>
                                  </VStack>
                                </CheckboxGroup>
                              </TabPanel>

                              {/* Account Age */}
                              <TabPanel>
                                <CheckboxGroup
                                  value={formData.segments.accountAge}
                                  onChange={(values) =>
                                    handleSegmentChange("accountAge", values as string[])
                                  }
                                >
                                  <VStack align="start" spacing={2}>
                                    <Checkbox value="new" colorScheme="cyan">
                                      New Users (&lt; 30 days)
                                    </Checkbox>
                                    <Checkbox value="recent" colorScheme="cyan">
                                      Recent Users (30-90 days)
                                    </Checkbox>
                                    <Checkbox value="established" colorScheme="cyan">
                                      Established Users (90-365 days)
                                    </Checkbox>
                                    <Checkbox value="longTerm" colorScheme="cyan">
                                      Long-term Users (1+ years)
                                    </Checkbox>
                                  </VStack>
                                </CheckboxGroup>
                              </TabPanel>

                              {/* Geographic Location */}
                              <TabPanel>
                                <CheckboxGroup
                                  value={formData.segments.geographicLocation}
                                  onChange={(values) =>
                                    handleSegmentChange("geographicLocation", values as string[])
                                  }
                                >
                                  <VStack align="start" spacing={2}>
                                    <Checkbox value="northAmerica" colorScheme="cyan">
                                      North America
                                    </Checkbox>
                                    <Checkbox value="europe" colorScheme="cyan">
                                      Europe
                                    </Checkbox>
                                    <Checkbox value="asia" colorScheme="cyan">
                                      Asia
                                    </Checkbox>
                                    <Checkbox value="oceania" colorScheme="cyan">
                                      Oceania
                                    </Checkbox>
                                    <Checkbox value="southAmerica" colorScheme="cyan">
                                      South America
                                    </Checkbox>
                                    <Checkbox value="africa" colorScheme="cyan">
                                      Africa
                                    </Checkbox>
                                  </VStack>
                                </CheckboxGroup>
                              </TabPanel>

                              {/* User Tier/Plan */}
                              <TabPanel>
                                <CheckboxGroup
                                  value={formData.segments.userTier}
                                  onChange={(values) =>
                                    handleSegmentChange("userTier", values as string[])
                                  }
                                >
                                  <VStack align="start" spacing={2}>
                                    <Checkbox value="free" colorScheme="cyan">
                                      Free Tier
                                    </Checkbox>
                                    <Checkbox value="basic" colorScheme="cyan">
                                      Basic Plan
                                    </Checkbox>
                                    <Checkbox value="premium" colorScheme="cyan">
                                      Premium Plan
                                    </Checkbox>
                                    <Checkbox value="enterprise" colorScheme="cyan">
                                      Enterprise
                                    </Checkbox>
                                  </VStack>
                                </CheckboxGroup>
                              </TabPanel>

                              {/* Engagement Level */}
                              <TabPanel>
                                <CheckboxGroup
                                  value={formData.segments.engagementLevel}
                                  onChange={(values) =>
                                    handleSegmentChange("engagementLevel", values as string[])
                                  }
                                >
                                  <VStack align="start" spacing={2}>
                                    <Checkbox value="highEngagement" colorScheme="cyan">
                                      Highly Engaged (daily logins)
                                    </Checkbox>
                                    <Checkbox value="moderateEngagement" colorScheme="cyan">
                                      Moderately Engaged
                                    </Checkbox>
                                    <Checkbox value="lowEngagement" colorScheme="cyan">
                                      Low Engagement
                                    </Checkbox>
                                  </VStack>
                                </CheckboxGroup>
                              </TabPanel>

                              {/* Portfolio Size */}
                              <TabPanel>
                                <CheckboxGroup
                                  value={formData.segments.portfolioSize}
                                  onChange={(values) =>
                                    handleSegmentChange("portfolioSize", values as string[])
                                  }
                                >
                                  <VStack align="start" spacing={2}>
                                    <Checkbox value="small" colorScheme="cyan">
                                      Small Portfolio (&lt; $10,000)
                                    </Checkbox>
                                    <Checkbox value="medium" colorScheme="cyan">
                                      Medium Portfolio ($10,000 - $100,000)
                                    </Checkbox>
                                    <Checkbox value="large" colorScheme="cyan">
                                      Large Portfolio (&gt; $100,000)
                                    </Checkbox>
                                  </VStack>
                                </CheckboxGroup>
                              </TabPanel>

                              {/* Broker Connection Status */}
                              <TabPanel>
                                <CheckboxGroup
                                  value={formData.segments.brokerConnection}
                                  onChange={(values) =>
                                    handleSegmentChange("brokerConnection", values as string[])
                                  }
                                >
                                  <VStack align="start" spacing={2}>
                                    <Checkbox value="connected" colorScheme="cyan">
                                      Connected to Broker
                                    </Checkbox>
                                    <Checkbox value="notConnected" colorScheme="cyan">
                                      Not Connected
                                    </Checkbox>
                                    <Checkbox value="connectionIssues" colorScheme="cyan">
                                      Connection Issues
                                    </Checkbox>
                                  </VStack>
                                </CheckboxGroup>
                              </TabPanel>
                            </TabPanels>
                          </Tabs>
                        </Box>
                      </MenuList>
                    </Menu>
                  )}

                  {formData.sentTo === "segmented" && (
                    <Box
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="md"
                      p={3}
                      bg={cardBg}
                    >
                      <Text
                        fontSize="xs"
                        color={secondaryTextColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        mb={3}
                      >
                        Backend audience filters
                      </Text>

                      {segmentDescriptor?.fields?.length ? (
                        <VStack align="stretch" spacing={3}>
                          {segmentDescriptor.fields.map((field) => {
                            const currentValue =
                              (formData.targetSegment || {})[field.id];
                            const hasValue =
                              currentValue !== undefined &&
                              currentValue !== null &&
                              !(Array.isArray(currentValue) && currentValue.length === 0) &&
                              currentValue !== "";

                            return (
                              <Box key={field.id}>
                                <Flex align="center" justify="space-between" mb={1}>
                                  <Text
                                    fontSize="sm"
                                    color={textColor}
                                    fontFamily="'IBM Plex Sans', sans-serif"
                                    fontWeight="medium"
                                  >
                                    {field.label || field.id}
                                  </Text>
                                  {hasValue && (
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      onClick={() => clearTargetSegmentField(field.id)}
                                      fontFamily="'IBM Plex Sans', sans-serif"
                                      color={secondaryTextColor}
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </Flex>

                                {field.type === "boolean" ? (
                                  <Select
                                    value={
                                      typeof currentValue === "boolean"
                                        ? currentValue
                                          ? "true"
                                          : "false"
                                        : ""
                                    }
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (!v) return clearTargetSegmentField(field.id);
                                      handleTargetSegmentChange(field.id, v === "true");
                                    }}
                                    bg={cardBg}
                                    borderColor={borderColor}
                                    size="sm"
                                  >
                                    <option value="">Any</option>
                                    <option value="true">True</option>
                                    <option value="false">False</option>
                                  </Select>
                                ) : field.type === "datetime" ? (
                                  <Input
                                    type="date"
                                    value={typeof currentValue === "string" ? currentValue : ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (!v) return clearTargetSegmentField(field.id);
                                      handleTargetSegmentChange(field.id, v);
                                    }}
                                    bg={cardBg}
                                    borderColor={borderColor}
                                    size="sm"
                                  />
                                ) : field.values && field.values.length > 0 ? (
                                  <CheckboxGroup
                                    value={
                                      Array.isArray(currentValue)
                                        ? (currentValue as string[])
                                        : []
                                    }
                                    onChange={(values) => {
                                      if (!values || (values as string[]).length === 0) {
                                        return clearTargetSegmentField(field.id);
                                      }
                                      handleTargetSegmentChange(field.id, values as string[]);
                                    }}
                                  >
                                    <VStack align="stretch" spacing={2}>
                                      {field.values.map((v) => (
                                        <Checkbox key={v} value={v}>
                                          {v}
                                        </Checkbox>
                                      ))}
                                    </VStack>
                                  </CheckboxGroup>
                                ) : (
                                  <Input
                                    value={typeof currentValue === "string" ? currentValue : ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (!v) return clearTargetSegmentField(field.id);
                                      handleTargetSegmentChange(field.id, v);
                                    }}
                                    placeholder="Enter value"
                                    bg={cardBg}
                                    borderColor={borderColor}
                                    size="sm"
                                  />
                                )}
                              </Box>
                            );
                          })}
                        </VStack>
                      ) : (
                        <Text
                          fontSize="xs"
                          color={secondaryTextColor}
                          fontFamily="'IBM Plex Sans', sans-serif"
                        >
                          Segmentation options are unavailable right now.
                        </Text>
                      )}
                    </Box>
                  )}
                </VStack>
              </FormControl>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                  <FormLabel
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={textColor}
                    fontSize="sm"
                  >
                    Action Link (Optional)
                  </FormLabel>
                  <Input
                    value={formData.actionLink}
                    onChange={(e) => handleInputChange("actionLink", e.target.value)}
                    placeholder="/dashboard/trades/123"
                    bg={cardBg}
                    borderColor={borderColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontSize="sm"
                  />
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    mt={1}
                    fontFamily="'IBM Plex Sans', sans-serif"
                  >
                    Deep link to a page in the app
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontWeight="medium"
                    color={textColor}
                    fontSize="sm"
                  >
                    Action Button Label
                  </FormLabel>
                  <Input
                    value={formData.actionLabel}
                    onChange={(e) => handleInputChange("actionLabel", e.target.value)}
                    placeholder="View Trade"
                    bg={cardBg}
                    borderColor={borderColor}
                    fontFamily="'IBM Plex Sans', sans-serif"
                    fontSize="sm"
                  />
                  <Text
                    fontSize="xs"
                    color={secondaryTextColor}
                    mt={1}
                    fontFamily="'IBM Plex Sans', sans-serif"
                  >
                    Button text (shown if link is set)
                  </Text>
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  color={textColor}
                >
                  Schedule Date & Time (Optional)
                </FormLabel>
                <Input
                  type="datetime-local"
                  value={formData.scheduledFor}
                  onChange={(e) => {
                    handleInputChange("scheduledFor", e.target.value);
                    if (e.target.value) {
                      handleInputChange("isScheduled", true);
                    }
                  }}
                  bg={cardBg}
                  borderColor={borderColor}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              fontFamily="'IBM Plex Sans', sans-serif"
            >
              Cancel
            </Button>
            {editingNotification?.status === "sent" ? (
              <Button
                bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                color="white"
                onClick={() => handleSaveNotification("draft")}
                fontFamily="'IBM Plex Sans', sans-serif"
                _hover={{ opacity: 0.9 }}
              >
                Update
              </Button>
            ) : (
              <HStack spacing={3}>
                <Button
                  variant="outline"
                  borderColor={borderColor}
                  color={textColor}
                  onClick={() => handleSaveNotification("draft")}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  textTransform="uppercase"
                  fontSize="xs"
                  _hover={{ bg: useColorModeValue("gray.50", "gray.700") }}
                >
                  Save as Draft
                </Button>
                <Button
                  bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                  color="white"
                  onClick={() => {
                    // Auto-check schedule if not already checked
                    if (!formData.isScheduled) {
                      handleInputChange("isScheduled", true);
                    }
                    handleSaveNotification("schedule");
                  }}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  textTransform="uppercase"
                  fontSize="xs"
                  _hover={{ opacity: 0.9 }}
                  isDisabled={!formData.scheduledFor}
                >
                  Schedule
                </Button>
                <Button
                  bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
                  color="white"
                  onClick={() => handleSaveNotification("send")}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  textTransform="uppercase"
                  fontSize="xs"
                  _hover={{ opacity: 0.9 }}
                >
                  Send Now
                </Button>
              </HStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xl">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          bg={cardBg}
          maxH="90vh"
          borderRadius="lg"
        >
          <ModalHeader
            pt={6}
            px={6}
            pb={4}
            borderBottom="1px solid"
            borderColor={borderColor}
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Text
                fontSize="2xl"
                fontWeight="500"
                color="#38BBD1"
                fontFamily="'Urbanist', sans-serif"
              >
                Notification Preview
              </Text>
              <ModalCloseButton position="static" />
            </Flex>
          </ModalHeader>
          <ModalBody p={0}>
            {previewNotification && (
              <Box p={6}>
                {/* Header - Matching NotificationModal details view */}
                <Flex justifyContent="space-between" alignItems="flex-start" mb={4}>
                  <HStack spacing={3} flex={1} minW={0}>
                    <Box fontSize="32px" flexShrink={0}>
                      {previewNotification.type === "trade" && "💰"}
                      {previewNotification.type === "system" && "⚠️"}
                      {previewNotification.type === "performance" && "🎯"}
                      {previewNotification.type === "market" && "📈"}
                      {previewNotification.type === "learning" && "📚"}
                      {previewNotification.type === "announcement" && "📢"}
                    </Box>
                    <Flex direction="column" flex={1} minW={0}>
                      <Text
                        fontSize="lg"
                        fontWeight="600"
                        color="#38BBD1"
                        fontFamily="'Urbanist', sans-serif"
                        mb={1}
                        noOfLines={2}
                      >
                        {previewNotification.title}
                      </Text>
                      <Badge
                        colorScheme={
                          previewNotification.type === "trade" ? "green" :
                          previewNotification.type === "system" ? "orange" :
                          previewNotification.type === "performance" ? "purple" :
                          previewNotification.type === "market" ? "cyan" :
                          previewNotification.type === "learning" ? "blue" :
                          "gray"
                        }
                        fontSize="xs"
                        fontFamily="'IBM Plex Sans', sans-serif"
                        textTransform="uppercase"
                        w="fit-content"
                        maxW="100%"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {previewNotification.type}
                      </Badge>
                    </Flex>
                  </HStack>
                </Flex>

                <Divider mb={4} borderColor={borderColor} />

                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="500"
                      color="#38BBD1"
                      mb={2}
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Message
                    </Text>
                    <Box
                      fontSize="sm"
                      color={textColor}
                      fontFamily="'Urbanist', sans-serif"
                      dangerouslySetInnerHTML={{ __html: previewNotification.message }}
                    />
                  </Box>

                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="500"
                      color="#38BBD1"
                      mb={2}
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Time
                    </Text>
                    <Text
                      fontSize="sm"
                      color={textColor}
                      fontFamily="'Urbanist', sans-serif"
                    >
                      {previewNotification.time || "Just now"}
                    </Text>
                  </Box>

                  {previewNotification.details && Object.keys(previewNotification.details).length > 0 && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="500"
                        color="#38BBD1"
                        mb={2}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                      >
                        Details
                      </Text>
                      <Box
                        bg={useColorModeValue("gray.50", "gray.700")}
                        p={4}
                        borderRadius="md"
                        fontFamily="'IBM Plex Sans', sans-serif"
                      >
                        {previewNotification.type === "trade" && previewNotification.details && (
                          <VStack spacing={3} align="stretch">
                            {previewNotification.details.symbol && (
                              <Flex justifyContent="space-between">
                                <Text fontSize="xs" color="#38BBD1" textTransform="uppercase">
                                  Symbol
                                </Text>
                                <Text fontSize="sm" fontWeight="600" color={textColor}>
                                  {previewNotification.details.symbol}
                                </Text>
                              </Flex>
                            )}
                            {previewNotification.details.entryPrice !== undefined && (
                              <Flex justifyContent="space-between">
                                <Text fontSize="xs" color="#38BBD1" textTransform="uppercase">
                                  Entry Price
                                </Text>
                                <Text fontSize="sm" color={textColor}>
                                  ${previewNotification.details.entryPrice?.toFixed(2)}
                                </Text>
                              </Flex>
                            )}
                            {previewNotification.details.exitPrice !== undefined && (
                              <Flex justifyContent="space-between">
                                <Text fontSize="xs" color="#38BBD1" textTransform="uppercase">
                                  Exit Price
                                </Text>
                                <Text fontSize="sm" color={textColor}>
                                  ${previewNotification.details.exitPrice?.toFixed(2)}
                                </Text>
                              </Flex>
                            )}
                            {previewNotification.details.quantity !== undefined && (
                              <Flex justifyContent="space-between">
                                <Text fontSize="xs" color="#38BBD1" textTransform="uppercase">
                                  Quantity
                                </Text>
                                <Text fontSize="sm" color={textColor}>
                                  {previewNotification.details.quantity} shares
                                </Text>
                              </Flex>
                            )}
                            {(previewNotification.details.profit !== undefined || previewNotification.details.loss !== undefined) && (
                              <Divider borderColor={borderColor} />
                            )}
                            {previewNotification.details.profit !== undefined && (
                              <Flex justifyContent="space-between" alignItems="center">
                                <Text fontSize="xs" color="#38BBD1" textTransform="uppercase">
                                  Profit
                                </Text>
                                <Text fontSize="lg" fontWeight="600" color="green.500">
                                  +${previewNotification.details.profit.toFixed(2)}
                                </Text>
                              </Flex>
                            )}
                            {previewNotification.details.loss !== undefined && (
                              <Flex justifyContent="space-between" alignItems="center">
                                <Text fontSize="xs" color="#38BBD1" textTransform="uppercase">
                                  Loss
                                </Text>
                                <Text fontSize="lg" fontWeight="600" color="red.500">
                                  -${Math.abs(previewNotification.details.loss).toFixed(2)}
                                </Text>
                              </Flex>
                            )}
                            {previewNotification.details.returnPercent !== undefined && (
                              <Flex justifyContent="space-between">
                                <Text fontSize="xs" color="#38BBD1" textTransform="uppercase">
                                  Return
                                </Text>
                                <Text
                                  fontSize="sm"
                                  fontWeight="600"
                                  color={previewNotification.details.returnPercent >= 0 ? "green.500" : "red.500"}
                                >
                                  {previewNotification.details.returnPercent >= 0 ? "+" : ""}
                                  {previewNotification.details.returnPercent.toFixed(2)}%
                                </Text>
                              </Flex>
                            )}
                          </VStack>
                        )}
                        {/* For other types, show generic details */}
                        {previewNotification.type !== "trade" && (
                          <VStack spacing={3} align="stretch">
                            {Object.entries(previewNotification.details).map(([key, value]) => {
                              if (value === undefined || value === null) return null;
                              return (
                                <Flex key={key} justifyContent="space-between">
                                  <Text fontSize="xs" color="#38BBD1" textTransform="uppercase">
                                    {key.replace(/([A-Z])/g, " $1").trim()}
                                  </Text>
                                  <Text fontSize="sm" color={textColor}>
                                    {typeof value === "object" ? JSON.stringify(value) : String(value)}
                                  </Text>
                                </Flex>
                              );
                            })}
                          </VStack>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Divider borderColor={borderColor} />

                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                      color="white"
                      fontWeight="medium"
                      borderRadius="8px"
                      px="20px"
                      py="10px"
                      h="auto"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      fontSize="sm"
                      flex={1}
                      _hover={{
                        bgGradient: "linear-gradient(135deg, #3EEAEC, #38BBD1)",
                      }}
                      _active={{
                        bgGradient: "linear-gradient(135deg, #38BBD1, #3EEAEC)",
                      }}
                    >
                      {previewNotification.actionLabel || "View Details"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor={borderColor}
                      color={textColor}
                      fontWeight="medium"
                      borderRadius="8px"
                      px="20px"
                      py="10px"
                      h="auto"
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      fontSize="sm"
                    >
                      Dismiss
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Templates Modal */}
      <Modal isOpen={isTemplateOpen} onClose={onTemplateClose} size="lg">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader
            color="#38BBD1"
            fontFamily="'Urbanist', sans-serif"
            fontWeight="bold"
            fontSize="lg"
          >
            Notification Templates
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const newTemplate: Notification = {
                    ...formData,
                    id: Date.now().toString(),
                    time: "Template",
                    createdAt: new Date().toISOString(),
                    status: "draft",
                    totalSent: 0,
                    totalOpened: 0,
                  };
                  setTemplates(prev => [...prev, newTemplate]);
                  toast.success("Template Saved", { description: "Notification saved as template." });
                }}
                fontFamily="'IBM Plex Sans', sans-serif"
                fontSize="xs"
                fontWeight="medium"
              >
                Save Current as Template
              </Button>
              {templates.length === 0 ? (
                <Text
                  fontSize="sm"
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  textAlign="center"
                  py={8}
                >
                  No templates saved yet. Create a notification and save it as a template.
                </Text>
              ) : (
                <VStack spacing={2} align="stretch">
                  {templates.map((template) => (
                    <Box
                      key={template.id}
                      p={3}
                      bg={useColorModeValue("gray.50", "gray.700")}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={borderColor}
                      _hover={{ bg: rowHoverBg }}
                      cursor="pointer"
                      onClick={() => {
                        handleCopyNotification(template);
                        onTemplateClose();
                      }}
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color={textColor}
                            fontFamily="'IBM Plex Sans', sans-serif"
                          >
                            {template.title}
                          </Text>
                          <Text
                            fontSize="xs"
                            color={secondaryTextColor}
                            fontFamily="'IBM Plex Sans', sans-serif"
                            noOfLines={1}
                          >
                            {template.message}
                          </Text>
                        </VStack>
                        {getTypeBadge(template.type)}
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={onTemplateClose}
              fontFamily="'IBM Plex Sans', sans-serif"
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default NotificationsCenter;

